/**
 * Экран режима тренировки переключения раскладок.
 *
 * Пользователю показывается, где именно раскладка меняется: фрагменты
 * перечислены с пометкой языка, потому что в сплошной строке границу видно
 * только тому, кто её уже знает. Клавиатуру экран не показывает (ТЗ FR-EN-26):
 * на ней поместилась бы одна раскладка из двух, а показ неверных клавиш хуже,
 * чем их отсутствие (ТЗ §8.1).
 *
 * Над текстом стоит число переключений, разбор по фрагментам — под ним: он
 * повторяет то же упражнение и потому уступает ему место (ТЗ FR-EN-84).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { LanguageCode } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import type { LayoutSwitchPlan } from '@type2x/feature-layout-switch';

import type { Runtime } from '../runtime.js';
import { DrillLayout } from './DrillLayout.js';
import { TypingScreen } from './TypingScreen.js';

export interface LayoutSwitchScreenProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly plan: LayoutSwitchPlan;
  readonly sessionId: SessionId;
  readonly onFinished: (run: TypingSession) => void;
}

/** Раскладки упражнения по порядку появления, без повторов. */
function languagesOf(plan: LayoutSwitchPlan): readonly LanguageCode[] {
  return [...new Set(plan.exercise.segments.map((segment) => segment.language))];
}

export function LayoutSwitchScreen({
  runtime,
  t,
  plan,
  sessionId,
  onFinished,
}: LayoutSwitchScreenProps): React.JSX.Element {
  return (
    <DrillLayout
      t={t}
      title={t('layoutSwitch.title')}
      now={
        <>
          {/*
            Метки раскладок стоят рядом с заданием всё время (ТЗ UI-16):
            шрифтом различимость `p` и `р`, `c` и `с` не обещается, поэтому о
            смене раскладки говорит слово, а не начертание.
          */}
          {languagesOf(plan).map((language) => (
            <span key={language} className="t2x-badge t2x-badge--layout">
              {t(`layoutSwitch.badge.${language}`)}
            </span>
          ))}
          <span className="t2x-muted">
            {t('layoutSwitch.count', { count: plan.exercise.boundaries.length })}
          </span>
        </>
      }
      details={
        <>
          <p className="t2x-muted">{t('layoutSwitch.explain')}</p>

          <ol className="t2x-segments" aria-label={t('layoutSwitch.segments')}>
            {plan.exercise.segments.map((segment, index) => (
              <li key={`${segment.start}`}>
                <span className="t2x-segment-language">
                  {t(`layoutSwitch.language.${segment.language}`)}
                </span>{' '}
                {segment.text}
                {index > 0 && plan.exercise.segments[index - 1]?.language !== segment.language && (
                  <span className="t2x-segment-mark"> {t('layoutSwitch.boundary')}</span>
                )}
              </li>
            ))}
          </ol>

          <p className="t2x-muted">{t('layoutSwitch.hint')}</p>
        </>
      }
    >
      <TypingScreen
        key={sessionId}
        runtime={runtime}
        t={t}
        title={t('layoutSwitch.typing')}
        hint={t('layoutSwitch.hint')}
        sessionId={sessionId}
        layoutId={plan.layoutId}
        targetText={plan.exercise.text}
        hintLevel={plan.hintLevel}
        compact
        onFinished={onFinished}
      />
    </DrillLayout>
  );
}
